import React, { useState, useEffect, memo } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useIntl } from '../../util/reactIntl';
import { FaHeart, FaRegHeart, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { TbManualGearbox } from 'react-icons/tb';
import { BsFuelPump } from 'react-icons/bs';
import { IoSpeedometerOutline } from 'react-icons/io5';
import { PiSteeringWheelFill } from 'react-icons/pi';

import { NamedLink, CircularFlag } from '../../components';
import { getCachedCountryFlag } from '../../util/countryFlagCache';
import { createSlug } from '../../util/urlHelpers';
import { addFavorite, removeFavorite } from '../../ducks/user.duck';
import { formatPriceData } from '../../util/currencyHelpers';

import css from './HomePageListingCard.module.css';

// Get badge class based on listing level
const getLevelBadgeClass = (level) => {
  if (!level) return css.badgeStandard;

  const lowerLevel = level.toLowerCase();
  switch (lowerLevel) {
    case 'featured_level':
      return css.badgeFeatured;
    case 'standard_level':
      return css.badgeStandard;
    case 'premium_level':
      return css.badgePremium;
    case 'premium_pro_level':
      return css.badgePremiumPro;
    default:
      return css.badgeStandard;
  }
};

// Get display text for listing level
const getLevelDisplayText = (level) => {
  if (!level) return 'Standard';

  const lowerLevel = level.toLowerCase();
  switch (lowerLevel) {
    case 'featured_level':
      return 'Featured';
    case 'standard_level':
      return 'Standard';
    case 'premium_level':
      return 'Premium';
    case 'premium_pro_level':
      return 'Premium Pro';
    default:
      return 'Standard';
  }
};

// Get badge class based on badge text (for custom badges like "Reduced")
const getBadgeClassFromText = (text) => {
  if (!text) return css.badgeStandard;

  const lowerText = text.toLowerCase();
  switch (lowerText) {
    case 'new':
      return css.badgeNew;
    case 'featured':
      return css.badgeFeatured;
    case 'premium':
      return css.badgePremium;
    case 'premium-pro':
      return css.badgePremiumPro;
    case 'reduced':
      return css.badgeReduced;
    default:
      return css.badgeStandard;
  }
};

// Price data formatter
const priceData = (price, currency, intl) => {
  if (price) {
    const { formattedPrice, priceTitle } = formatPriceData(price, intl);
    return { formattedPrice, priceTitle };
  }
  return {};
};

/**
 * HomePageListingCard - Card for homepage with NewListingCard design + RecentListingCard price reduction
 * Note: This card does NOT have compare functionality (for homepage use)
 *
 * @param {Object} props
 * @param {Object} props.listing - Processed listing data with images as URLs
 * @param {Object} props.rawListing - Raw listing data from API
 * @param {string} props.badgeText - Custom badge text (e.g., "New", "Reduced", "Featured")
 */
const HomePageListingCard = ({
  listing,
  rawListing,
  badgeText
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const currentUser = useSelector(state => state.user.currentUser);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [countryFlagData, setCountryFlagData] = useState(null);

  const images = listing.images || [];
  const slug = createSlug(listing.title || '');

  // Check if listing is in user's favorites
  const favoriteListings = currentUser?.attributes?.profile?.privateData?.favoriteListings || [];
  const isFavorite = favoriteListings.includes(listing.id);

  // Get public data from raw listing for price reduction check
  const publicData = rawListing?.attributes?.publicData || {};
  const price = rawListing?.attributes?.price;
  const priceReduction = publicData?.priceReduction;
  const isPOA = publicData?.isPOA;

  // Check if this is a reduced price listing
  const isReduced = badgeText?.toLowerCase() === 'reduced' && priceReduction?.isActive;

  // Fetch country flag data from raw listing
  useEffect(() => {
    let isMounted = true;

    const fetchCountryFlag = async () => {
      if (!rawListing) {
        return;
      }

      try {
        const flagData = await getCachedCountryFlag(rawListing);
        if (isMounted) {
          setCountryFlagData(flagData);
        }
      } catch (error) {
        console.warn('Error fetching country flag:', error);
        if (isMounted) {
          setCountryFlagData(null);
        }
      }
    };

    if (rawListing && rawListing.id) {
      fetchCountryFlag();
    } else {
      setCountryFlagData(null);
    }

    return () => {
      isMounted = false;
    };
  }, [rawListing?.id?.uuid]);

  const hasMultipleImages = images.length > 1;

  const nextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const previousImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  // Handle favorite toggle
  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!currentUser) {
      history.push('/login');
      return false;
    }

    try {
      if (isFavorite) {
        dispatch(removeFavorite(listing.id));
      } else {
        dispatch(addFavorite(listing.id));
      }
    } catch (error) {
      console.error('Error in favorite toggle:', error);
    }

    return false;
  };

  // Render price section - handles both normal and reduced pricing
  const renderPrice = () => {
    // POA (Price on Application)
    if (isPOA) {
      return (
        <div className={css.priceNormal}>
          <span className={css.priceValue}>POA</span>
        </div>
      );
    }

    // Reduced price display
    if (isReduced && priceReduction?.previousPrice) {
      const { formattedPrice: formattedCurrentPrice } = priceData(price, price?.currency, intl);
      const { formattedPrice: formattedPreviousPrice } = priceData(
        priceReduction.previousPrice,
        priceReduction.previousPrice.currency,
        intl
      );

      return (
        <div className={css.priceReductionContainer}>
          <div className={css.previousPrice}>{formattedPreviousPrice}</div>
          <div className={css.currentPrice}>{formattedCurrentPrice}</div>
          {priceReduction?.reductionPercentage && (
            <div className={css.savingsText}>Save {priceReduction.reductionPercentage}%</div>
          )}
        </div>
      );
    }

    // Normal price display
    return (
      <p className={css.price}>{listing.price}</p>
    );
  };

  // Determine which badge to show
  const getBadgeInfo = () => {
    if (badgeText) {
      return {
        className: getBadgeClassFromText(badgeText),
        text: badgeText
      };
    }
    if (listing.status) {
      return {
        className: getLevelBadgeClass(listing.status),
        text: getLevelDisplayText(listing.status)
      };
    }
    return null;
  };

  const badgeInfo = getBadgeInfo();

  return (
    <div className={css.card}>
      {/* Image Section */}
      <NamedLink name="ListingPage" params={{ id: listing.id, slug }}>
        <div className={css.imageContainer}>
          {images.length > 0 && (
            <img
              src={images[currentImageIndex]}
              alt={listing.title}
              className={css.image}
              width={400}
              height={300}
              loading="lazy"
              decoding="async"
            />
          )}

          {/* Badges */}
          <div className={css.badgeContainer}>
            {badgeInfo && (
              <div className={badgeInfo.className}>
                {badgeInfo.text}
              </div>
            )}
            {rawListing?.attributes?.publicData?.sold ? (
              <div className={css.badgeSold}>
                Sold
              </div>
            ) : rawListing?.attributes?.publicData?.reserved ? (
              <div className={css.badgeReserved}>
                Reserved
              </div>
            ) : null}
          </div>

          {/* Favorite Button */}
          <button
            className={css.favoriteButton}
            onClick={handleFavoriteClick}
            onMouseDown={(e) => e.preventDefault()}
            onMouseUp={(e) => e.preventDefault()}
            type="button"
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            {isFavorite ? (
              <FaHeart className={css.heartIconFilled} />
            ) : (
              <FaRegHeart className={css.heartIcon} />
            )}
          </button>

          {/* Image Navigation */}
          {hasMultipleImages && (
            <>
              <button
                className={classNames(css.navButton, css.prevButton)}
                onClick={previousImage}
                type="button"
              >
                <FaChevronLeft className={css.navIcon} />
              </button>
              <button
                className={classNames(css.navButton, css.nextButton)}
                onClick={nextImage}
                type="button"
              >
                <FaChevronRight className={css.navIcon} />
              </button>

              {/* Dot Indicators */}
              <div className={css.indicators}>
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => goToImage(index, e)}
                    className={classNames(css.indicator, {
                      [css.indicatorActive]: index === currentImageIndex,
                    })}
                    type="button"
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>

              {/* Image Counter */}
              <div className={css.imageCounter}>
                <span className={css.counterText}>
                  {currentImageIndex + 1}/{images.length}
                </span>
              </div>
            </>
          )}
        </div>
      </NamedLink>

      {/* Card Content */}
      <div className={css.content}>
        {/* Header - Country Info */}
        <div className={css.header}>
          <div className={css.countryInfo}>
            {countryFlagData && (
              <>
                <CircularFlag countryCode={countryFlagData.countryCode} size="medium" />
                <span className={css.countryText}>{countryFlagData.countryName}</span>
              </>
            )}
          </div>
        </div>

        {/* Title - Fixed height for consistency */}
        <h2 className={css.title}>{listing.title}</h2>

        {/* Specs Row 1 - Always reserve space */}
        <div className={css.specsRow}>
          {listing.year && (
            <div className={css.specItem}>
              <span className={css.specText}>{listing.year}</span>
            </div>
          )}
          {listing.transmission && (
            <div className={css.specItem}>
              <TbManualGearbox className={css.specIcon} />
              <span className={css.specText}>{listing.transmission}</span>
            </div>
          )}
          {listing.fuelType && (
            <div className={css.specItem}>
              <BsFuelPump className={css.specIcon} />
              <span className={css.specText}>{listing.fuelType}</span>
            </div>
          )}
        </div>

        {/* Specs Row 2 - Always reserve space */}
        <div className={css.specsRow}>
          {listing.mileage && (
            <div className={css.specItem}>
              <IoSpeedometerOutline className={css.specIcon} />
              <span className={css.specText}>{listing.mileage}</span>
            </div>
          )}
          {listing.drivingSide && (
            <div className={css.specItem}>
              <PiSteeringWheelFill className={css.specIcon} />
              <span className={css.specText}>{listing.drivingSide}</span>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className={css.divider}></div>

        {/* Price and Action */}
        <div className={css.footer}>
          <div className={css.priceWrapper}>
            {renderPrice()}
          </div>
          <NamedLink name="ListingPage" params={{ id: listing.id, slug }}>
            <button className={css.viewButton}>View Details</button>
          </NamedLink>
        </div>
      </div>
    </div>
  );
};

export default memo(HomePageListingCard);
